import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login, isAuthed } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (isAuthed) return <Navigate to="/mypersonalworkplace" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/mypersonalworkplace", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[hsl(189_82%_9%)] border-r border-border/60 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <Logo size={44} rounded="rounded-2xl" />
          <div>
            <div className="font-display text-2xl tracking-tight">Amoreska</div>
            <div className="text-xs text-muted-foreground">Admin Console</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl leading-tight max-w-md">
            Run the community with confidence.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-md">
            Moderate members, review verifications, resolve reports and keep Amoreska safe — all
            from one fast, focused dashboard.
          </p>
        </div>
        <div className="text-xs text-muted-foreground relative">Secure admin access · v1.0</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Logo size={44} rounded="rounded-2xl" />
            <div className="font-display text-2xl">Amoreska</div>
          </div>

          <h1 className="font-display text-3xl tracking-tight">Sign in</h1>
          <p className="text-muted-foreground text-sm mt-1.5 mb-8">
            Enter your admin credentials to continue.
          </p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@amoreska.app"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </div>
          </div>

          {error && (
            <div
              className="mt-4 flex items-center gap-2 rounded-lg bg-destructive/15 text-destructive-foreground/90 ring-1 ring-destructive/40 px-3 py-2.5 text-sm"
              data-testid="login-error"
            >
              <ShieldAlert className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full mt-6 h-11 text-sm font-semibold"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
